import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {ApplicationCommonService} from '../../services/application.common.service';
import {
  approveApplication,
  approveApplicationFailure,
  approveApplicationSuccess,
  createApplication,
  createApplicationFailure,
  createApplicationSuccess,
  fakeSignApplication,
  fakeSignApplicationSuccess,
  loadApplication,
  loadApplicationFailure,
  loadApplicationList,
  loadApplicationListFailure,
  loadApplicationListSuccess,
  loadApplicationSuccess,
  loadPayments,
  loadPaymentsFailure,
  loadPaymentsSuccess,
  loadServiceAttachmentPositions,
  loadServiceAttachmentPositionsFailure,
  loadServiceAttachmentPositionsSuccess,
  sendApplication,
  sendApplicationFailure,
  sendApplicationSuccess,
  signApplication,
  signApplicationFailure,
  signApplicationSuccess,
  updateApplicantData,
  updateApplicantDataSuccess,
  updateApplication,
  updateApplicationAttachments,
  updateApplicationAttachmentsFailure,
  updateApplicationAttachmentsSuccess,
  updateApplicationFailure,
  updateApplicationSuccess,
  updateContractors,
  updateContractorsFailure,
  updateContractorsSuccess,
  updateInvestorRepresentatives,
  updateInvestorRepresentativesFailure,
  updateInvestorRepresentativesSuccess,
  updateParcels,
  updateParcelsFailure,
  updateParcelsSuccess,
  updateTaxObjectsData,
  updateTaxObjectsDataFailure,
  updateTaxObjectsDataSuccess
} from '../actions/application.actions';
import {catchError, map, of, switchMap} from 'rxjs';
import {ApplicationService} from '../../openapi';
import {InvestorRepresentativeCommonService} from '../../services/investor-representative.common.service';
import {ForestCommonService} from '../../services/forest.common.service';
import {ParcelsCommonService} from "../../services/parcels.common.service";

@Injectable()
export class ApplicationEffects {
  constructor(
    private actions$: Actions,
    private applicationCommonService: ApplicationCommonService,
    private applicationService: ApplicationService,
    private investorRepresentativeCommonService: InvestorRepresentativeCommonService,
    private forestCommonService: ForestCommonService,
    private parcelsCommonService: ParcelsCommonService
  ) {
  }

  loadServiceAttachmentPositions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadServiceAttachmentPositions),
      switchMap((action) =>
        this.applicationCommonService.loadAttachmentPositionsByApplicationId(action.appId)
          .pipe(
            map(serviceAttachmentPositions => loadServiceAttachmentPositionsSuccess({serviceAttachmentPositions})),
            catchError(() => of(loadServiceAttachmentPositionsFailure()))
          )
      )
    )
  );

  createApplication$ = createEffect(() => this.actions$.pipe(
    ofType(createApplication),
    switchMap(action =>
      this.applicationCommonService.createApplication(action.serviceTypeId, action?.application)
        .pipe(
          map(application => {
            action.successFnc(application);
            return createApplicationSuccess({application});
          }),
          catchError(() => {
            action.failureFnc();
            return of(createApplicationFailure());
          })
        )
    )
  ));

  loadApplication$ = createEffect(() => this.actions$.pipe(
    ofType(loadApplication),
    switchMap(action =>
      this.applicationService.getApplicationItemApplicationItem({
        id: action.id
      }).pipe(
        map(application => loadApplicationSuccess({application})),
        catchError(() => of(loadApplicationFailure()))
      )
    )
  ));

  updateApplicationData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateApplication),
      switchMap(action =>
        this.applicationService.patchApplicationApplicationItem({id: action.id, application: action.application})
          .pipe(
            map(application => {
              action.successFnc(application);
              return updateApplicationSuccess({application});
            }),
            catchError(() => {
              action.failureFnc();
              return of(updateApplicationFailure());
            })
          )
      )
    )
  );

  updateApplicants$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateApplicantData),
      switchMap(action =>
        this.applicationCommonService.updateApplicantData(action.application, action.applicationPayload, action.applicantPayload)
          .pipe(
            map(application => {
              action.successFnc(application?.uuid || '');
              return updateApplicantDataSuccess({application});
            }),
            catchError(() => {
              action.failureFnc();
              return of(updateApplicationFailure());
            })
          )
      )
    )
  );

  updateContractors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateContractors),
      switchMap(action =>
        this.applicationCommonService.updateContractors(action.applicationId, action.contractors)
          .pipe(
            map(application => {
              action.successFnc(application?.uuid || '');
              return updateContractorsSuccess({application});
            }),
            catchError(() => {
              action.failureFnc();
              return of(updateContractorsFailure());
            })
          )
      )
    )
  );

  updateForestsData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateTaxObjectsData),
      switchMap(action =>
        this.forestCommonService.updateForestsData(action.application, action.taxObjects)
          .pipe(
            map(application => {
              action.successFnc(application?.uuid || '');
              return updateTaxObjectsDataSuccess({application});
            }),
            catchError(() => of(updateTaxObjectsDataFailure()))
          )
      )
    )
  );

  updateApplicationAttachments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateApplicationAttachments),
      switchMap(action =>
        this.applicationCommonService.updateApplicationAttachments(action.application, action.attachments)
          .pipe(
            map(application => {
              action.successFnc(application?.uuid || '');
              return updateApplicationAttachmentsSuccess({application});
            }),
            catchError(() => {
              action.failureFnc();
              return of(updateApplicationAttachmentsFailure());
            })
          )
      )
    )
  );

  loadApplications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadApplicationList),
      switchMap(action =>
        this.applicationCommonService.loadApplicationList(action.code).pipe(
          map(applicationList => loadApplicationListSuccess({applicationList})),
          catchError(() => of(loadApplicationListFailure()))
        )
      )
    )
  );

  approveApplication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(approveApplication),
      switchMap(action =>
        this.applicationService.approveApplicationApplicationItem({
          id: action.applicationId,
          application: {}
        }).pipe(
          map(application => {
            action.successFnc();
            return approveApplicationSuccess({
              application
            });
          }),
          catchError(() => {
            action.failureFnc();
            return of(approveApplicationFailure());
          })
        )
      )
    )
  );

  loadPayments$ = createEffect(() => this.actions$.pipe(
    ofType(loadPayments),
    switchMap(action =>
      this.applicationCommonService.loadApplicationPayments(action.applicationId)
        .pipe(
          map(applicationPayment => loadPaymentsSuccess({applicationPayment})),
          catchError(() => of(loadPaymentsFailure()))
        )
    )
  ));

  sendApplication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sendApplication),
      switchMap(action => this.applicationService.sendApplicationApplicationItem({
          id: action.applicationId,
          application: {}
        }).pipe(
          map(() => {
            action.successFnc();
            return sendApplicationSuccess();
          }),
          catchError(() => {
            action.failureFnc();
            return of(sendApplicationFailure());
          })
        )
      )
    )
  );

  signApplication$ = createEffect(() => this.actions$.pipe(
    ofType(signApplication),
    switchMap(action =>
      this.applicationCommonService.signApplication(action.applicationId).pipe(
        map(result => {
          action.successFnc(result.redirect_url);
          return signApplicationSuccess();
        }),
        catchError(() => of(signApplicationFailure()))
      )
    )
  ));

  fakeSignApplication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fakeSignApplication),
      switchMap(action =>
        this.applicationService.fakeSignApplicationApplicationItem({id: action.applicationId, application: {}})
          .pipe(
            switchMap((application) => {
              action.successFnc();
              return [fakeSignApplicationSuccess(), loadApplication({id: action.applicationId})];
            }),
            catchError(() => {
              action.failureFnc();
              return of(signApplicationFailure());
            })
          )
      )
    )
  );

  updateInvestorRepresentatives$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateInvestorRepresentatives),
      switchMap(action =>
        this.investorRepresentativeCommonService.updateInvestorRepresentative(action.currentApp, action.payload, action.investorRepresentatives)
          .pipe(
            map(application => {
              action.successFnc(application.uuid || '');
              return updateInvestorRepresentativesSuccess({application});
            }),
            catchError(() => {
              action.failureFnc();
              return of(updateInvestorRepresentativesFailure());
            })
          )
      )
    )
  );

  updateParcels$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateParcels),
      switchMap((action) =>
        this.parcelsCommonService.updateParcels(action.currentApp, action.parcels).pipe(
          map(application => {
            action.successFnc(application?.uuid || '');
            return updateParcelsSuccess({application});
          }),
          catchError(() => {
            action.failureFnc();
            return of(updateParcelsFailure());
          })
        )
      )
    )
  );

}
